import {
  CircularProgress,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import STRING_KEYS from '../../../language/keys';
import TableHeadContainer from '../../../components/Table/TableHead';
import TableContainer from '../../../components/Table/TableContainer';
import { TALENT_PARAMS } from '../../../consts/requestParamsKeys';

import { Sort } from '../../../interfaces/apiRequest.interface';
import useNavigate from '../../../hooks/useNavigate';
import { TalentsRowItem } from './TalentsRowItem';
import TalentsEmptyState from './TalentsEmptyState';
import TalentsCounter from './TalentsCounter';

type TalentsTableProps = {
  talentsData: TalentsListResponse,
  error: unknown | boolean,
  onPageChnage: (newPageNumber: number) => void,
  isLoading: boolean,
  page: number,
  activeSort: Sort,
  onSortClick: (val: Sort) => void,
}

export const TalentsTable = ({
  talentsData,
  error,
  onPageChnage,
  isLoading,
  page,
  activeSort,
  onSortClick,
}: TalentsTableProps):JSX.Element => {
  const { t: translate } = useTranslation();

  const { goToTalentView } = useNavigate();

  const headers = [
    {
      key: TALENT_PARAMS.FULL_NAME,
      value: translate(STRING_KEYS.TALENTS_TABLE_HEADER_NAME_AND_ID),
      isSortable: true,
    },
    {
      key: TALENT_PARAMS.CURRENT_COMPANY_NAME,
      value: translate(STRING_KEYS.TALENTS_TABLE_HEADER_CURRENT_COMPANY),
      isSortable: true,
    },
    {
      key: TALENT_PARAMS.CURRENT_JOB_TITLE,
      value: translate(STRING_KEYS.TALENTS_TABLE_HEADER_JOB_TITLE),
      isSortable: true,
    },
    {
      key: TALENT_PARAMS.DATE_UPDATED,
      value: translate(STRING_KEYS.ACCOUNTS_TABLE_HEADER_LAST_UPDATE),
      isSortable: true,
    },
    {
      key: TALENT_PARAMS.DATE_CREATED,
      value: translate(STRING_KEYS.ACCOUNTS_TABLE_HEADER_DATE_CREATED),
      isSortable: true,
    },
    {
      key: 'action',
      value: '',
    },
  ];

  const handleChangePage = (
    e:React.MouseEvent<HTMLButtonElement> | null,
    newPageNumber: number,
  ):void => {
    onPageChnage(newPageNumber + 1);
  };

  // todo MATAN: common component
  const renderTalentCounter = (): JSX.Element => {
    if (!talentsData || talentsData.totalCount <= 0) {
      return <TalentsEmptyState tableColumnsCount={ headers.length } />;
    }

    return (
      <TalentsCounter
        total={ talentsData.totalCount }
        tableColumnsCount={ headers.length }
      />
    );
  };

  const renderTableBody = () => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell
            align="center"
            colSpan={ headers.length }
          >
            <CircularProgress />
          </TableCell>
        </TableRow>
      );
    }

    return talentsData?.results.map((talent:Talent) => {
      return (
        <TalentsRowItem
          key={ talent.talentId }
          talent={ talent }
          onRowClick={ () => {
            goToTalentView(talent.talentId);
          } }
        />
      );
    });
  };

  if (error) {
    return (
      <div>{ translate(STRING_KEYS.TALENTS_ERROR) }</div>
    );
  }

  return (
    <TableContainer
      totalCount={ talentsData ? talentsData.totalCount : 0 }
      onPageChange={ handleChangePage }
      page={ page }
      isLoading={ isLoading }
      paginationType={ talentsData?.type }
      hasNextPage={ talentsData?.hasNextPage }
    >
      <TableHeadContainer
        headers={ headers }
        order={ activeSort }
        orderHandler={ onSortClick }
      />

      <TableBody>
        {renderTalentCounter()}
        {renderTableBody()}
      </TableBody>
    </TableContainer>
  );
};
